import React from "react";
import {
	Row,
	Col,
	Pagination,
	PaginationItem,
	PaginationLink,
	FormGroup,
	Label,
	Input
} from "reactstrap";

interface Props {
	pagination: {
		page: number;
		size: number;
		elements: number;
		first: boolean;
		last: boolean;
	};
	pageChanged: (which: "first" | "prev" | "next" | "last" | number) => void;
	onChangeInSelect: (evt: React.ChangeEvent<HTMLInputElement>) => void;
	pageSize: number;
	t: (key: string) => string;
}

const PaginationControls: React.FC<Props> = ({
	pagination,
	pageChanged,
	onChangeInSelect,
	pageSize,
	t
}) => {
	const { page, size, elements, first, last } = pagination;
	const totalPages = Math.ceil(elements / size);
	const pageNumbers = Array.from({ length: totalPages }, (_, i) => i + 1);

	const start = elements > 0 ? page * size + 1 : 0;
	const end = Math.min((page + 1) * size, elements);

	return (
		<Row className="align-items-center mt-3">
			<Col xs="12" md="4">
				<div className="text-muted">
					{t("common.pagination.showing")}{" "}
					<span className="fw-semibold">
						{start}-{end} {t("common.pagination.of")} {elements}
					</span>{" "}
					{t("common.pagination.results")}
				</div>
			</Col>

			<Col xs="12" md="4" className="text-center">
				<Pagination className="pagination-separated pagination-sm justify-content-center mb-0">
					<PaginationItem disabled={first}>
						<PaginationLink first onClick={() => pageChanged("first")} />
					</PaginationItem>
					<PaginationItem disabled={first}>
						<PaginationLink previous onClick={() => pageChanged("prev")} />
					</PaginationItem>

					{pageNumbers.map((pNum) => (
						<PaginationItem active={pNum - 1 === page} key={pNum}>
							<PaginationLink onClick={() => pageChanged(pNum - 1)}>
								{pNum}
							</PaginationLink>
						</PaginationItem>
					))}

					<PaginationItem disabled={last}>
						<PaginationLink next onClick={() => pageChanged("next")} />
					</PaginationItem>
					<PaginationItem disabled={last}>
						<PaginationLink last onClick={() => pageChanged("last")} />
					</PaginationItem>
				</Pagination>
			</Col>

			<Col xs="12" md="4" className="text-md-end mt-2 mt-md-0">
				<FormGroup className="d-inline-flex align-items-center mb-0">
					<Label for="pageSize" className="me-2 mb-0 small text-muted">
						{t("common.pagination.show")}
					</Label>
					<Input
						id="pageSize"
						type="select"
						value={pageSize}
						onChange={onChangeInSelect}
						className="form-control-sm"
						style={{ width: "auto" }}
					>
						{[10, 20, 30, 50, 100].map((n) => (
							<option key={n} value={n}>{n}</option>
						))}
					</Input>
					<span className="ms-2 small text-muted">
						{t("common.pagination.perPage")}
					</span>
				</FormGroup>
			</Col>
		</Row>
	);
};

export default PaginationControls;
